import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import { isPaymentBankBlocked } from '@/utils/paymentBankAvailability'

const PAYKUMO_FUNCTIONS_BASE = 'https://asia-east2-time2-lucky.cloudfunctions.net'
const FINAL_STATUSES = ['SUCCESS', 'FAILED', 'EXPIRED', 'CANCELLED', 'CREATE_FAILED', 'MANUAL_REVIEW']
const TRUE_WALLET_BANK_KEYS = ['truewallet', 'truemoney', 'truemoneywallet', 'ทรูวอลเลท', 'ทรูมันนี่']

function normalizeBank (value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
}

function requestId () {
  return `paykumo-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

function paymentUrl (value) {
  try {
    const url = new URL(String(value || '').trim())
    return url.protocol === 'https:' ? url.href : ''
  } catch (error) {
    return ''
  }
}

export default {
  data () {
    return {
      paykumoConfig: {
        enabled: false,
        depositEnabled: false,
        availableNow: false,
        depositMin: 300,
        depositMax: 100000,
        currency: 'THB',
        visibleToCodes: [],
        blockedBanks: []
      },
      paykumoConfigLoaded: false,
      paykumoTransaction: null,
      paykumoOrderId: '',
      paykumoRedirectUrl: '',
      paykumoClientRequestId: '',
      paykumoStatusTimer: null,
      paykumoStatusChecking: false,
      paykumoCompletionHandled: false
    }
  },
  computed: {
    hasActivePaykumoDeposit () {
      return Boolean(
        this.paykumoOrderId &&
        this.paykumoTransaction &&
        (this.paykumoTransaction.qr_image || this.paykumoShowsReceiverAccount || this.paykumoRedirectUrl)
      )
    },
    paykumoShowsReceiverAccount () {
      return this.hasCompletePaykumoReceiverDetails(this.paykumoTransaction)
    },
    paykumoDepositRangeText () {
      return `${Number(this.paykumoConfig.depositMin || 300).toLocaleString()}-${Number(this.paykumoConfig.depositMax || 100000).toLocaleString()}`
    },
    paykumoVisibleToUser () {
      const codes = Array.isArray(this.paykumoConfig.visibleToCodes)
        ? this.paykumoConfig.visibleToCodes.map(code => String(code || '').trim().toUpperCase()).filter(Boolean)
        : []
      const userCode = String((this.user && this.user.code) || '').trim().toUpperCase()
      return codes.includes('*') || Boolean(userCode && codes.includes(userCode))
    },
    paykumoSelectedDepositAccount () {
      const approved = typeof this.getApprovedAccounts === 'function' ? this.getApprovedAccounts() : []
      return this.selectedDepositAccount || approved[0] || null
    },
    paykumoAvailableForSelectedAccount () {
      return !this.isPaykumoBlockedDepositAccount(this.paykumoSelectedDepositAccount)
    }
  },
  watch: {
    'user.key': {
      immediate: true,
      handler (userKey) {
        if (!userKey) return
        this.loadPaykumoConfig()
        this.restorePaykumoDeposit()
      }
    }
  },
  beforeDestroy () {
    this.stopPaykumoStatusPolling()
  },
  methods: {
    async paykumoHeaders () {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบใหม่')
      return { Authorization: `Bearer ${await currentUser.getIdToken()}` }
    },
    async loadPaykumoConfig () {
      try {
        const response = await axios.get(`${PAYKUMO_FUNCTIONS_BASE}/paykumoPublicConfig`, { timeout: 10000 })
        if (response.data && response.data.success && response.data.data) {
          this.paykumoConfig = { ...this.paykumoConfig, ...response.data.data }
        } else {
          throw new Error('Invalid Paykumo public config response')
        }
      } catch (error) {
        this.paykumoConfig = { ...this.paykumoConfig, enabled: false, depositEnabled: false, availableNow: false }
        console.warn('Paykumo public config unavailable:', error && error.message ? error.message : error)
      } finally {
        this.paykumoConfigLoaded = true
      }
    },
    isPaykumoAmountAllowed (amount) {
      const value = Number(amount || 0)
      const min = Number(this.paykumoConfig.depositMin || 300)
      const max = Number(this.paykumoConfig.depositMax || 100000)
      return Number.isInteger(value) && value >= min && (max <= 0 || value <= max)
    },
    isPaykumoBlockedDepositAccount (account) {
      return TRUE_WALLET_BANK_KEYS.includes(normalizeBank(account && (account.bank || account.bank_name))) ||
        isPaymentBankBlocked(this.paykumoConfig.blockedBanks, account)
    },
    async tryPaykumoDeposit (selectedAccount) {
      await this.loadPaykumoConfig()
      if (this.paykumoConfig.availableNow !== true) return false
      if (this.isPaykumoBlockedDepositAccount(selectedAccount)) return false
      if (!this.paykumoConfig.enabled || !this.paykumoConfig.depositEnabled || !this.paykumoVisibleToUser) return false
      const amount = Number.parseInt(this.inputDepositAmount, 10) || 0
      if (!this.isPaykumoAmountAllowed(amount)) throw new Error(`ไม่สามารถสร้างการฝากได้ ตอนนี้ระบบรองรับยอดฝาก ${this.paykumoDepositRangeText} บาท ต่อรายการเท่านั้น`)
      const clientRequestId = this.preparePaykumoRequestId(amount, selectedAccount)
      let response
      try {
        response = await axios.post(`${PAYKUMO_FUNCTIONS_BASE}/paykumoCreateDeposit`, {
          userKey: this.user.key,
          amount,
          account: selectedAccount,
          bonus: this.gotpro ? 'Y' : 'N',
          clientRequestId
        }, { headers: await this.paykumoHeaders(), timeout: 45000 })
      } catch (error) {
        const status = Number(error && error.response && error.response.status)
        if (status > 0 && status < 500) this.clearPaykumoDepositCache()
        throw new Error(this.paykumoErrorMessage(error))
      }
      if (response.data && response.data.manualReview) {
        throw new Error(response.data.error || 'Paykumo กำลังตรวจสอบรายการเดิม กรุณาอย่าสร้างรายการซ้ำและติดต่อแอดมิน')
      }
      const data = response.data && response.data.data
      if (
        !response.data ||
        !response.data.success ||
        !data ||
        !data.order_id ||
        (!(data.qr_image || data.redirect_url || data.address) && !this.hasCompletePaykumoReceiverDetails(data))
      ) {
        throw new Error((response.data && response.data.error) || 'Paykumo response ไม่ครบถ้วน')
      }
      this.depositAmount = amount
      this.depositAmountWithDecimal = amount.toLocaleString('en-US')
      this.depositDecimal = '00'
      this.assignedBank = null
      this.paybridgeMatch = null
      if (!this.applyPaykumoTransaction({
        ...data,
        amount_requested: amount,
        client_request_id: clientRequestId,
        account_fingerprint: this.paykumoAccountFingerprint(selectedAccount)
      })) throw new Error('ไม่สามารถบันทึกรายการฝาก Paykumo ได้')
      return true
    },
    paykumoErrorMessage (error) {
      const payload = error && error.response && error.response.data
      const provider = payload && payload.provider
      if (provider && Array.isArray(provider.fields) && provider.fields.length) return provider.fields.join(', ')
      return (provider && (provider.message || provider.detail)) || (payload && payload.error) || (error && error.message) || 'Paykumo ไม่สามารถสร้างรายการฝากได้ กรุณาลองใหม่'
    },
    paykumoStorageKey () {
      return `time2lucky_paykumo_deposit_${(this.user && this.user.key) || 'guest'}`
    },
    paykumoReceiverDetails (transaction) {
      const source = transaction || {}
      return {
        receiver_account: String(source.receiver_account || source.receiverAccount || '').trim(),
        receiver_name: String(source.receiver_name || source.receiverName || '').trim(),
        receiver_bank: String(source.receiver_bank || source.receiverBank || '').trim(),
        receiver_bank_branch: String(source.receiver_bank_branch || source.receiverBankBranch || '').trim()
      }
    },
    hasCompletePaykumoReceiverDetails (transaction) {
      const receiver = this.paykumoReceiverDetails(transaction)
      return Boolean(receiver.receiver_account && receiver.receiver_name && receiver.receiver_bank)
    },
    paykumoAccountFingerprint (account) {
      return [account && (account.bank || account.bank_name), account && (account.account_no || account.accountNo || account.account)]
        .map(value => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, ''))
        .join('|')
    },
    preparePaykumoRequestId (amount, account) {
      const fingerprint = this.paykumoAccountFingerprint(account)
      let cached = null
      if (typeof window !== 'undefined' && window.localStorage) {
        try { cached = JSON.parse(localStorage.getItem(this.paykumoStorageKey()) || 'null') } catch (error) {}
      }
      const reusable = cached && cached.client_request_id &&
        Number(cached.amount || 0) === Number(amount || 0) &&
        String(cached.account_fingerprint || '') === fingerprint &&
        Date.now() - Number(cached.stored_at || 0) < 15 * 60 * 1000
      this.paykumoClientRequestId = reusable ? cached.client_request_id : requestId()
      if (!reusable && typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(this.paykumoStorageKey(), JSON.stringify({
          client_request_id: this.paykumoClientRequestId,
          amount: Number(amount || 0),
          account_fingerprint: fingerprint,
          stored_at: Date.now()
        }))
      }
      return this.paykumoClientRequestId
    },
    persistPaykumoDeposit (transaction) {
      if (typeof window === 'undefined' || !window.localStorage || !transaction) return
      localStorage.setItem(this.paykumoStorageKey(), JSON.stringify({
        order_id: transaction.order_id || this.paykumoOrderId,
        redirect_url: transaction.redirect_url || transaction.address || this.paykumoRedirectUrl,
        qr_image: transaction.qr_image || '',
        expires_at: Number(transaction.expires_at) || null,
        client_request_id: transaction.client_request_id || this.paykumoClientRequestId,
        account_fingerprint: transaction.account_fingerprint || '',
        ...this.paykumoReceiverDetails(transaction),
        amount_requested: Number(transaction.amount_requested || transaction.amount || this.depositAmount || 0),
        stored_at: Date.now()
      }))
    },
    clearPaykumoDepositCache () {
      if (typeof window !== 'undefined' && window.localStorage) localStorage.removeItem(this.paykumoStorageKey())
    },
    applyPaykumoTransaction (transaction) {
      if (!transaction || !transaction.order_id) return false
      const previous = this.paykumoOrderId === transaction.order_id ? (this.paykumoTransaction || {}) : {}
      transaction = { ...previous, ...transaction }
      const redirectUrl = paymentUrl(transaction.redirect_url || transaction.address)
      const receiverDetails = this.paykumoReceiverDetails(transaction)
      if (!transaction.qr_image && !redirectUrl && !this.hasCompletePaykumoReceiverDetails(receiverDetails)) return false
      this.paykumoTransaction = {
        ...transaction,
        ...receiverDetails,
        redirect_url: redirectUrl
      }
      this.paykumoOrderId = transaction.order_id
      this.paykumoRedirectUrl = redirectUrl
      this.paykumoClientRequestId = transaction.client_request_id || this.paykumoClientRequestId
      this.depositAmount = Number(transaction.amount_requested || transaction.amount || this.depositAmount || 0)
      this.depositAmountWithDecimal = Number(this.depositAmount || 0).toLocaleString('en-US')
      this.assignedBank = null
      this.persistPaykumoDeposit(this.paykumoTransaction)
      this.startPaykumoStatusPolling(transaction.order_id)
      return true
    },
    startPaykumoStatusPolling (orderId) {
      if (!orderId) return
      this.stopPaykumoStatusPolling()
      this.paykumoCompletionHandled = false
      const poll = () => this.fetchPaykumoDepositStatus(orderId)
      poll()
      this.paykumoStatusTimer = setInterval(poll, 4000)
    },
    stopPaykumoStatusPolling () {
      if (this.paykumoStatusTimer) clearInterval(this.paykumoStatusTimer)
      this.paykumoStatusTimer = null
    },
    async fetchPaykumoDepositStatus (orderId) {
      if (!orderId || !(this.user && this.user.key) || this.paykumoCompletionHandled || this.paykumoStatusChecking) return null
      this.paykumoStatusChecking = true
      try {
        const response = await axios.post(`${PAYKUMO_FUNCTIONS_BASE}/paykumoGetDepositStatus`, {
          userKey: this.user.key,
          orderId
        }, { headers: await this.paykumoHeaders(), timeout: 15000 })
        const transaction = response.data && response.data.data
        if (!response.data || !response.data.success || !transaction) return null
        if (this.paykumoOrderId !== orderId) return null
        const previous = this.paykumoTransaction || {}
        this.paykumoTransaction = { ...(this.paykumoTransaction || {}), ...transaction }
        if (this.paykumoTransaction.qr_image !== previous.qr_image || this.paykumoTransaction.expires_at !== previous.expires_at) {
          this.persistPaykumoDeposit(this.paykumoTransaction)
        }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') this.handlePaykumoDepositSuccess(transaction)
        if (['FAILED', 'EXPIRED', 'CREATE_FAILED', 'MANUAL_REVIEW'].includes(status)) this.handlePaykumoDepositFailed(transaction, status)
        return transaction
      } catch (error) {
        return null
      } finally {
        this.paykumoStatusChecking = false
      }
    },
    async restorePaykumoDeposit () {
      if (typeof window === 'undefined' || !window.localStorage || !(this.user && this.user.key)) return false
      try {
        const cached = JSON.parse(localStorage.getItem(this.paykumoStorageKey()) || 'null')
        if (!cached || !cached.order_id) return false
        const response = await axios.post(`${PAYKUMO_FUNCTIONS_BASE}/paykumoGetDepositStatus`, {
          userKey: this.user.key,
          orderId: cached.order_id
        }, { headers: await this.paykumoHeaders(), timeout: 15000 })
        const statusData = response.data && response.data.data
        if (!response.data || !response.data.success || !statusData) {
          this.clearPaykumoDepositCache()
          return false
        }
        const transaction = { ...cached, ...statusData, order_id: cached.order_id }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') return this.handlePaykumoDepositSuccess(transaction)
        if (['FAILED', 'EXPIRED', 'CREATE_FAILED', 'MANUAL_REVIEW'].includes(status)) return this.handlePaykumoDepositFailed(transaction, status)
        if (FINAL_STATUSES.includes(status)) {
          this.clearPaykumoDepositCache()
          return false
        }
        return this.applyPaykumoTransaction(transaction)
      } catch (error) {
        this.clearPaykumoDepositCache()
        return false
      }
    },
    handlePaykumoDepositSuccess (transaction) {
      if (this.paykumoCompletionHandled) return true
      this.paykumoCompletionHandled = true
      this.stopPaykumoStatusPolling()
      this.clearPaykumoDepositCache()
      const amount = Number(transaction.actual_amount || transaction.amount_requested || this.depositAmount || 0)
      this.resetPaykumoDepositState()
      if (typeof window !== 'undefined' && window.$) window.$('#depositModal').modal('hide')
      setTimeout(() => this.$swal({ icon: 'success', title: 'ฝากเงินสำเร็จ', text: `ระบบเติมเครดิต ${amount.toLocaleString()} บาทเรียบร้อยแล้ว` }), 200)
      return true
    },
    handlePaykumoDepositFailed (transaction, status) {
      if (this.paykumoCompletionHandled) return true
      this.paykumoCompletionHandled = true
      this.stopPaykumoStatusPolling()
      this.clearPaykumoDepositCache()
      const reason = transaction.failed_reason || transaction.credit_error || transaction.paykumo_error || ''
      this.resetPaykumoDepositState()
      if (typeof window !== 'undefined' && window.$) window.$('#depositModal').modal('hide')
      setTimeout(() => this.$swal({
        icon: status === 'MANUAL_REVIEW' ? 'warning' : 'error',
        title: status === 'MANUAL_REVIEW' ? 'กำลังตรวจสอบรายการฝาก' : 'รายการฝากไม่สำเร็จ',
        text: typeof reason === 'string' ? (reason || 'กรุณาทำรายการใหม่หรือติดต่อแอดมิน') : 'กรุณาติดต่อแอดมิน'
      }), 200)
      return true
    },
    resetPaykumoDepositState () {
      this.paykumoTransaction = null
      this.paykumoOrderId = ''
      this.paykumoRedirectUrl = ''
      this.paykumoClientRequestId = ''
      this.paykumoStatusChecking = false
      this.paykumoCompletionHandled = false
    },
    async cancelPaykumoDeposit (options = {}) {
      const orderId = this.paykumoOrderId
      if (orderId) await this.fetchPaykumoDepositStatus(orderId)
      if (!this.paykumoOrderId) return
      const confirmation = options.skipConfirmation ? { isConfirmed: true } : await this.$swal({
        icon: 'warning',
        title: 'ยกเลิกรายการและเปลี่ยนยอดฝาก?',
        text: 'หากโอนไปแล้วอย่ายกเลิก กรุณารอระบบยืนยันรายการสำเร็จ',
        showCancelButton: true,
        confirmButtonText: 'ยืนยันยกเลิก',
        cancelButtonText: 'รอทำรายการต่อ'
      })
      if (!confirmation.isConfirmed) return
      try {
        const response = await axios.post(`${PAYKUMO_FUNCTIONS_BASE}/paykumoCancelDeposit`, {
          userKey: this.user.key,
          orderId
        }, { headers: await this.paykumoHeaders(), timeout: 15000 })
        if (!response.data || !response.data.success) {
          throw new Error((response.data && response.data.error) || 'ไม่สามารถยกเลิกรายการฝากได้')
        }
        if (response.data.alreadyCompleted) {
          await this.fetchPaykumoDepositStatus(orderId)
          return false
        }
      } catch (error) {
        console.warn('Close Paykumo deposit failed:', error && error.message ? error.message : error)
        if (this.$swal) {
          await this.$swal({
            icon: 'error',
            title: 'ยังไม่สามารถสร้างรายการใหม่ได้',
            text: 'ระบบปิดรายการฝากเดิมไม่สำเร็จ กรุณาลองอีกครั้ง'
          })
        }
        return false
      }
      this.stopPaykumoStatusPolling()
      this.clearPaykumoDepositCache()
      this.resetPaykumoDepositState()
      this.depositAmount = null
      this.depositAmountWithDecimal = null
      this.inputDepositAmount = null
      return true
    }
  }
}
